import * as R from 'ramda'
import {
  AssessmentEquipmentGroupAnswers,
  AssessmentQuestion,
  BuildingQuestion,
  BuildingQuestionKey,
  ClientFrontlineConfig,
  Elevator,
  ElevatorQuestion,
  Equipment,
  EquipmentGroup,
  Escalator,
  EscalatorQuestion,
  Offer,
  OpportunityCategory,
  ElevatorAssessmentQuestion,
  ElevatorQuestionKey,
  EquipmentGroupStId,
  EquipmentCategory
} from '../../../../types/types'
import { Building } from '../../../../types/types'
import { AssessmentErrors, Module } from './assessment-errors'
import { getElevatorValidation, getEscalatorValidation, Validator, getBuildingValidation } from './validations'
import {
  getEquipmentForGroup,
  getEscalatorCountForOffer,
  getDoorCountForOffer,
  vasAddendumHasNoEscalators,
  isMarketSegmentMissing,
  isFundingSectorMissing,
  getPeopleCountForOffer
} from '../offer'
import {
  translateXLAssessmentQuestionAnswerKey,
  getFrontlineEnabledTechnicalPlatforms,
  getFrontlineEnabledEscalatorTypes
} from '../frontline-config/utils'

export interface AssessmentProgress {
  buildingCount: number
  buildingsProgress: number
  communicationProgress: number
  elevatorCount: number
  elevatorProgress: number
  errors: AssessmentErrors
  escalatorCount: number
  escalatorProgress: number
  isValid: boolean
  isValidContact: boolean
  isValidMasterData: (category?: EquipmentCategory | 'building') => boolean
  isValidForQuickTender: boolean
  progress: number
  progressByGroup: Record<EquipmentGroupStId, number>
  isValidOpportunity: boolean
  isOnlyPeopleflowOffer: boolean
}

export function getAssessmentProgress(offer: Offer, frontlineConfig: ClientFrontlineConfig): AssessmentProgress {
  const { buildings, selectedInteractionRating, assessmentEquipmentGroupAnswers, opportunity } = offer
  const { opportunityCategory } = opportunity

  const errors = new AssessmentErrors()
  const buildingQuestions = getEditableQuestions(frontlineConfig.buildingQuestions)
  const elevatorQuestions = getEditableQuestions(frontlineConfig.elevatorQuestions)
  const escalatorQuestions = getEditableQuestions(frontlineConfig.escalatorQuestions)
  const assessmentQuestions = frontlineConfig.assessmentQuestions
  // XL assessment answers are stored in normal keys, so we need to translate them here
  const assessmentElevatorQuestions = assessmentQuestions.elevator_xl.all.map(
    (q): ElevatorAssessmentQuestion => ({
      ...q,
      subject: 'elevator',
      key: translateXLAssessmentQuestionAnswerKey(q.key) as ElevatorQuestionKey
    })
  )

  const assessmentXLGroupQuestions = frontlineConfig.assessmentQuestions.group.all
  const groupQuestions = assessmentXLGroupQuestions

  const buildingCount = buildings.length
  let equipmentGroupCount = 0
  const peopleflowEquipmentGroupCount = 0
  let elevatorCount = 0
  let escalatorCount = 0

  let buildingDetailsAnswerCount = 0
  let equipmentGroupAssessmentAnswerCount = 0
  let equipmentGroupAssessmentAnswerCountSalesIdCategory = 0
  let mandatoryBuildingDetailsAnswerCount = 0
  let mandatoryEquipmentGroupAssessmentAnswerCount = 0

  let elevatorAnswerCount = 0
  let elevatorAssessmentAnswerCount = 0
  let mandatoryElevatorAnswerCount = 0
  let mandatoryElevatorAssessmentAnswerCount = 0

  let escalatorAnswerCount = 0
  const escalatorAssessmentAnswerCount = 0
  let mandatoryEscalatorAnswerCount = 0
  const mandatoryEscalatorAssessmentAnswerCount = 0

  const doorAnswerCount = 0
  const doorAssessmentAnswerCount = 0
  const mandatoryDoorAnswerCount = 0
  const mandatoryDoorAssessmentAnswerCount = 0

  const resiflowAnswerCount = 0
  const mandatoryResiflowAnswerCount = 0

  for (const building of buildings) {
    const buildingId = building.salesToolId

    for (const question of buildingQuestions) {
      const questionStatus = isBuildingQuestionValid(question, building)
      switch (questionStatus) {
        case 'invalid':
        case 'notAnswered':
          if (question.mandatory) {
            errors.addError({
              category: 'building',
              explanation: 'Question is mandatory',
              id: buildingId,
              module: 'details',
              parentId: '',
              questionKey: question.key
            })
          }
          break
        case 'valid':
          buildingDetailsAnswerCount += 1
          if (question.mandatory) {
            mandatoryBuildingDetailsAnswerCount += 1
          }
      }
    }

    for (const equipment of building.details.equipment) {
      switch (equipment.equipmentCategory) {
        case 'elevator': {
          const frontlineEnabledTechnicalPlatforms = getFrontlineEnabledTechnicalPlatforms(frontlineConfig)
          elevatorCount += 1

          for (const question of elevatorQuestions) {
            const questionStatus = isElevatorQuestionValid(question, equipment, frontlineEnabledTechnicalPlatforms)
            switch (questionStatus) {
              case 'invalid':
                errors.addError({
                  category: equipment.equipmentCategory,
                  explanation: 'Question is not valid',
                  id: equipment.salesToolId,
                  module: 'details',
                  parentId: building.salesToolId,
                  questionKey: question.key
                })
                break
              case 'notAnswered':
                if (question.mandatory) {
                  errors.addError({
                    category: equipment.equipmentCategory,
                    explanation: 'Question is mandatory',
                    id: equipment.salesToolId,
                    module: 'details',
                    parentId: building.salesToolId,
                    questionKey: question.key
                  })
                }
                break
              case 'valid':
                elevatorAnswerCount += 1
                if (question.mandatory) {
                  mandatoryElevatorAnswerCount += 1
                }
            }
          }

          for (const question of assessmentElevatorQuestions) {
            const questionStatus = isAssessmentEquipmentQuestionValid(question, equipment, equipment.startsPerYear)
            switch (questionStatus) {
              case 'invalid':
                errors.addError({
                  category: equipment.equipmentCategory,
                  explanation: 'Question is not valid',
                  id: equipment.salesToolId,
                  module: question.display_group as Module,
                  parentId: building.salesToolId,
                  questionKey: question.key
                })
                break
              case 'notAnswered':
                if (question.mandatory_by_category[opportunityCategory]) {
                  errors.addError({
                    category: equipment.equipmentCategory,
                    explanation: 'Question is mandatory in category',
                    id: equipment.salesToolId,
                    module: question.display_group as Module,
                    parentId: building.salesToolId,
                    questionKey: question.key
                  })
                }
                break
              case 'valid':
                elevatorAssessmentAnswerCount += 1
                if (question.mandatory_by_category[opportunityCategory]) {
                  mandatoryElevatorAssessmentAnswerCount += 1
                }
            }
          }
          break
        }

        case 'escalator': {
          const frontlineEnabledEscalatorTypes = getFrontlineEnabledEscalatorTypes(frontlineConfig)
          escalatorCount += 1

          for (const question of escalatorQuestions) {
            const questionStatus = isEscalatorQuestionValid(question, equipment, frontlineEnabledEscalatorTypes)
            switch (questionStatus) {
              case 'invalid':
                errors.addError({
                  category: equipment.equipmentCategory,
                  explanation: 'Question is not valid',
                  id: equipment.salesToolId,
                  module: 'details',
                  parentId: building.salesToolId,
                  questionKey: question.key
                })
                break
              case 'notAnswered':
                if (question.mandatory) {
                  errors.addError({
                    category: equipment.equipmentCategory,
                    explanation: 'Question is mandatory',
                    id: equipment.salesToolId,
                    module: 'details',
                    parentId: building.salesToolId,
                    questionKey: question.key
                  })
                }
                break
              case 'valid':
                escalatorAnswerCount += 1
                if (question.mandatory) {
                  mandatoryEscalatorAnswerCount += 1
                }
            }
          }
          break
        }
      }
    }
  }
  const progressByGroup: Record<EquipmentGroupStId, number> = {}
  for (const salesToolId in offer.equipmentGroups) {
    const equipmentGroup = offer.equipmentGroups[salesToolId]
    equipmentGroupCount += 1
    equipmentGroupAssessmentAnswerCountSalesIdCategory = 0
    for (const question of groupQuestions) {
      const questionStatus = isAssessmentEquipmentGroupQuestionValid(
        question,
        equipmentGroup,
        assessmentEquipmentGroupAnswers
      )
      switch (questionStatus) {
        case 'invalid':
          errors.addError({
            category: 'group',
            explanation: 'Question is invalid',
            id: salesToolId,
            module: question.display_group as Module,
            parentId: '',
            questionKey: question.key
          })
          break
        case 'notAnswered':
          if (question.mandatory_by_category[opportunityCategory]) {
            errors.addError({
              category: 'group',
              explanation: 'Question is invalid',
              id: salesToolId,
              module: question.display_group as Module,
              parentId: '',
              questionKey: question.key
            })
          }
          break
        case 'valid':
          equipmentGroupAssessmentAnswerCount += 1
          equipmentGroupAssessmentAnswerCountSalesIdCategory += 1
          if (question.mandatory_by_category[opportunityCategory]) {
            mandatoryEquipmentGroupAssessmentAnswerCount += 1
          }
      }
    }
    const equipmentGroupAssessmentQuestionCountSalesIdCategory = groupQuestions.length
    const groupProgress = calculateProgress(
      equipmentGroupAssessmentQuestionCountSalesIdCategory,
      equipmentGroupAssessmentAnswerCountSalesIdCategory
    )
    progressByGroup[salesToolId] = groupProgress
  }

  const buildingQuestionCount = buildingQuestions.length * buildingCount
  const elevatorQuestionCount = elevatorQuestions.length * elevatorCount
  const escalatorQuestionCount = escalatorQuestions.length * escalatorCount

  const mandatoryBuildingQuestionCount = getNumberOfMandatoryQuestions(buildingQuestions) * buildingCount
  const mandatoryElevatorQuestionCount = getNumberOfMandatoryQuestions(elevatorQuestions) * elevatorCount
  const mandatoryEscalatorQuestionCount = getNumberOfMandatoryQuestions(escalatorQuestions) * escalatorCount

  const equipmentGroupAssessmentQuestionCount = groupQuestions.length * equipmentGroupCount
  const elevatorAssessmentQuestionCount = assessmentElevatorQuestions.length * elevatorCount

  const mandatoryEquipmentGroupAssessmentQuestionCount =
    getNumberOfMandatoryAssessmentQuestions(groupQuestions, opportunityCategory) * equipmentGroupCount
  const mandatoryElevatorAssessmentQuestionCount =
    getNumberOfMandatoryAssessmentQuestions(assessmentElevatorQuestions, opportunityCategory) * elevatorCount

  const isOnlyPeopleflowOffer = equipmentGroupCount === 0 && peopleflowEquipmentGroupCount > 0

  let communicationQuestionCount = 1
  if (isOnlyPeopleflowOffer) {
    communicationQuestionCount = 0
  }

  const communicationAnswerCount =
    selectedInteractionRating != null && frontlineConfig.interactionScopes.includes(selectedInteractionRating) ? 1 : 0

  const questionCount =
    buildingQuestionCount +
    equipmentGroupAssessmentQuestionCount +
    elevatorQuestionCount +
    elevatorAssessmentQuestionCount +
    escalatorQuestionCount +
    communicationQuestionCount

  const mandatoryQuestionCount =
    mandatoryBuildingQuestionCount +
    mandatoryEquipmentGroupAssessmentQuestionCount +
    mandatoryElevatorQuestionCount +
    mandatoryElevatorAssessmentQuestionCount +
    mandatoryEscalatorQuestionCount +
    communicationQuestionCount

  const answerCount =
    buildingDetailsAnswerCount +
    equipmentGroupAssessmentAnswerCount +
    elevatorAnswerCount +
    elevatorAssessmentAnswerCount +
    escalatorAnswerCount +
    escalatorAssessmentAnswerCount +
    doorAnswerCount +
    doorAssessmentAnswerCount +
    resiflowAnswerCount +
    communicationAnswerCount

  const mandatoryAnswerCount =
    mandatoryBuildingDetailsAnswerCount +
    mandatoryEquipmentGroupAssessmentAnswerCount +
    mandatoryElevatorAnswerCount +
    mandatoryElevatorAssessmentAnswerCount +
    mandatoryEscalatorAnswerCount +
    mandatoryEscalatorAssessmentAnswerCount +
    mandatoryDoorAnswerCount +
    mandatoryResiflowAnswerCount +
    mandatoryDoorAssessmentAnswerCount +
    communicationAnswerCount

  const buildingsProgress = calculateProgress(
    buildingQuestionCount + equipmentGroupAssessmentQuestionCount,
    buildingDetailsAnswerCount + equipmentGroupAssessmentAnswerCount
  )

  const communicationProgress = calculateProgress(communicationQuestionCount, communicationAnswerCount)

  const elevatorProgress = calculateProgress(
    elevatorQuestionCount + elevatorAssessmentQuestionCount,
    elevatorAnswerCount + elevatorAssessmentAnswerCount
  )

  const escalatorProgress = calculateProgress(
    escalatorQuestionCount,
    escalatorAnswerCount + escalatorAssessmentAnswerCount
  )
  const everyBuildingHasEquipment =
    buildings.length > 0 && buildings.every(building => building.details.equipment.length > 0)

  const everyGroupHasEquipment = R.values(offer.equipmentGroups).every(
    group => getEquipmentForGroup(group.salesToolId, buildings).length > 0
  )

  const startsPerYearOk = isStartsPerYearXLAnswered(frontlineConfig, buildings)

  const isValidMasterData = (category?: EquipmentCategory | 'building'): boolean => {
    if (!category) {
      // If no category is given, look through every option
      return (
        isValidMasterData('building') && isValidMasterData('elevator') && isValidMasterData('escalator')
        // isValidMasterData('door') &&
        // isValidMasterData('resiflow')
      )
    }

    // / TODO: use same validations in data grid & this
    const noDetailErrors = errors.countErrorsBy({ module: 'details', category }) === 0
    if (category === 'building') {
      return noDetailErrors && everyBuildingHasEquipment
    } else if (category === 'elevator') {
      return noDetailErrors && startsPerYearOk
    } else if (category === 'escalator') {
      return noDetailErrors && vasAddendumHasNoEscalators(offer)
    } else {
      return false
    }
  }
  const isContactsValid = offer.contacts.every(contact => Boolean(contact.role))
  const isValidContact =
    !offer.completed && !offer.finalization_started && !offer.finalized
      ? offer.contacts.length > 0 && isContactsValid
      : true
  const isValid = isValidMasterData() && mandatoryQuestionCount === mandatoryAnswerCount && everyGroupHasEquipment

  const atLeastOneQuickTenderScopeEnabled =
    R.pathOr([], ['quickTender', 'scopes'], frontlineConfig).filter(s => s !== 'value_added_services_addendum').length >
    0

  const isValidForQuickTender =
    atLeastOneQuickTenderScopeEnabled &&
    getEscalatorCountForOffer(buildings) === 0 && // Quick tender is not for escalators
    getDoorCountForOffer(buildings) === 0 && // Quick tender is not for doors either
    getPeopleCountForOffer(buildings) === 0 &&
    everyBuildingHasEquipment &&
    everyGroupHasEquipment &&
    mandatoryElevatorAnswerCount === mandatoryElevatorQuestionCount &&
    buildings.length > 0 &&
    // startsPerYearOk &&
    offer.assessmentMethod != null &&
    false // !isEscalationWithVasAddendumOffer(offer)

  const progress = calculateProgress(questionCount, answerCount)

  const isValidOpportunity = !isMarketSegmentMissing(opportunity) && !isFundingSectorMissing(opportunity)

  return {
    buildingCount,
    buildingsProgress,
    communicationProgress,
    errors,
    escalatorCount,
    escalatorProgress,
    elevatorCount,
    elevatorProgress,
    isValidMasterData,
    isValid,
    isValidContact,
    isValidForQuickTender,
    progress,
    progressByGroup,
    isValidOpportunity,
    isOnlyPeopleflowOffer
  }
}

function isStartsPerYearXLAnswered(frontlineConfig: ClientFrontlineConfig, buildings: Building[]): boolean {
  const startsPerYearXLQuestion = frontlineConfig.assessmentQuestions.elevator_xl.all.find(
    question => question.key === 'STARTS_PER_YEAR_XL'
  )!
  if (!startsPerYearXLQuestion) {
    return true
  }
  const assessmentElevatorQuestions: ElevatorAssessmentQuestion = {
    ...startsPerYearXLQuestion,
    subject: 'elevator',
    key: translateXLAssessmentQuestionAnswerKey(startsPerYearXLQuestion.key) as ElevatorQuestionKey
  }
  return buildings.every(({ details: { equipment } }) =>
    equipment
      .filter(e => e.equipmentCategory === 'elevator')
      .every(e => isAssessmentEquipmentQuestionValid(assessmentElevatorQuestions, e, e.startsPerYear) === 'valid')
  )
}

type validationResult = 'notAnswered' | 'invalid' | 'valid'

function isBuildingQuestionValid(question: BuildingQuestion, building: Building): validationResult {
  const { key } = question
  const answer = building.details[key]

  if (typeof answer !== 'undefined') {
    const validator = getBuildingValidation(question)
    if (validator) {
      return validator.validate(answer) ? 'valid' : 'invalid'
    } else {
      return 'invalid'
    }
  }

  return 'notAnswered'
}

function isElevatorQuestionValid(
  question: ElevatorQuestion,
  elevator: Elevator,
  frontlineEnabledTechnicalPlatforms: string[]
): validationResult {
  const { key } = question
  const answer = elevator[key]
  const { isAPFFromCRM } = elevator
  if (typeof answer !== 'undefined') {
    const validator = getElevatorValidation(question, frontlineEnabledTechnicalPlatforms, isAPFFromCRM)
    return validator.validate(answer) ? 'valid' : 'invalid'
  }
  return 'notAnswered'
}

function isEscalatorQuestionValid(
  question: EscalatorQuestion,
  escalator: Escalator,
  frontlineEnabledEscalatorTypes: string[]
): validationResult {
  const { key } = question
  const answer = escalator[key]
  if (typeof answer !== 'undefined') {
    const validator: Validator<string | number | null> = getEscalatorValidation(
      question,
      escalator.equipmentType,
      frontlineEnabledEscalatorTypes
    )
    return validator.validate(answer) ? 'valid' : 'invalid'
  }
  return 'notAnswered'
}

function isAssessmentEquipmentGroupQuestionValid(
  question: AssessmentQuestion,
  equipmentGroup: EquipmentGroup,
  assessmentAnswers: AssessmentEquipmentGroupAnswers
): validationResult {
  const { salesToolId } = equipmentGroup
  const answer = assessmentAnswers[salesToolId] && assessmentAnswers[salesToolId][question.key as BuildingQuestionKey]
  if (answer != null) {
    return question.choices.find(choice => choice.key === answer) != null ? 'valid' : 'invalid'
  }
  return 'notAnswered'
}

function isAssessmentEquipmentQuestionValid(
  question: AssessmentQuestion,
  equipment: Equipment,
  assessmentAnswers: string
): validationResult {
  if (assessmentAnswers != null) {
    return question.choices.find(choice => choice.key === Number(assessmentAnswers)) != null ? 'valid' : 'invalid'
  }
  return 'notAnswered'
}

function getEditableQuestions<T extends { readonly: boolean }>(questions: T[]): T[] {
  return questions.filter(question => !question.readonly)
}

function getNumberOfMandatoryQuestions<T extends { mandatory: boolean }>(questions: T[]): number {
  let sum = 0

  for (const question of questions) {
    if (question.mandatory) {
      sum += 1
    }
  }

  return sum
}

function getNumberOfMandatoryAssessmentQuestions(
  questions: AssessmentQuestion[],
  opportunityCategory: OpportunityCategory
): number {
  let sum = 0

  for (const question of questions) {
    if (question.mandatory_by_category[opportunityCategory]) {
      sum += 1
    }
  }

  return sum
}

export function calculateProgress(questionCount: number, answerCount: number): number {
  if (answerCount === 0) {
    return 0
  }

  return Math.floor((answerCount / questionCount) * 100)
}
